import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { bootstrapAPI } from '../utils/supabase'
import { getDetectedTimezone } from '../utils/timezoneUtils'

/**
 * Transform bootstrap data from API format to app format
 */
function transformBootstrapData(data) {
  const bootstrapData = data || {}

  // Transform user_settings from snake_case to camelCase
  let userSettings = null
  if (bootstrapData.user_settings) {
    const us = bootstrapData.user_settings
    userSettings = {
      timezone: us.timezone || getDetectedTimezone(),
      dateFormat: us.date_format ?? 'MM/dd/yyyy',
      timeFormat: us.time_format ?? '12h',
      themePreference: us.theme_preference ?? 'system',
      showDailyFocus: us.show_daily_focus ?? true,
      showInbox: us.show_inbox ?? true,
      showCritical: us.show_critical ?? true,
      showOverdue: us.show_overdue ?? true,
      showFlagged: us.show_flagged ?? true,
      showDueSoon: us.show_due_soon ?? true,
      showStuck: us.show_stuck ?? true,
      showQuickWin: us.show_quick_win ?? true,
      attentionThresholds: us.attention_thresholds,
      savedKanbanFilters: us.saved_kanban_filters || [],
      excludeWeekendsFromMetrics: us.exclude_weekends_from_metrics ?? false,
      selectedHolidayCalendars: us.selected_holiday_calendars || [],
      customExcludedDates: us.custom_excluded_dates || [],
      onboarding_completed: us.onboarding_completed ?? false,
      realtime_enabled: us.realtime_enabled ?? false
    }
  } else {
    // Default settings with detected timezone
    const detectedTimezone = getDetectedTimezone()
    userSettings = {
      timezone: detectedTimezone,
      showDailyFocus: true,
      showInbox: true,
      showCritical: true,
      showOverdue: true,
      showFlagged: true,
      showDueSoon: true,
      showStuck: true,
      showQuickWin: true,
      themePreference: 'system',
      savedKanbanFilters: [],
      excludeWeekendsFromMetrics: false,
      selectedHolidayCalendars: [],
      customExcludedDates: [],
      onboarding_completed: false,
      realtime_enabled: false
    }
  }

  return {
    userSettings,
    tasks: bootstrapData.tasks || [],
    projects: bootstrapData.projects || [],
    taskStatuses: bootstrapData.task_statuses || [],
    boardColumns: bootstrapData.board_columns || [],
    columnStatusMappings: bootstrapData.column_status_mappings || [],
    annualGoals: bootstrapData.annual_goals || [],
    quarterlyGoals: bootstrapData.quarterly_goals || [],
    weeklyGoals: bootstrapData.weekly_goals || [],
    taskLabels: bootstrapData.task_labels || [],
    // Wiki data
    wikiSpaces: bootstrapData.wiki_spaces || [],
    wikiPages: bootstrapData.wiki_pages || []
  }
}

/**
 * Async thunk to fetch all bootstrap data in a single call
 * Error handling is simplified - let errors propagate to refreshManager's retry logic
 */
export const fetchBootstrapData = createAsyncThunk(
  'bootstrap/fetch',
  async (userId, { rejectWithValue }) => {
    try {
      const { data, error } = await bootstrapAPI.getBootstrapData(userId)

      if (error) {
        const errorMessage = error?.message || String(error)
        console.log('[Bootstrap] API error:', errorMessage)
        return rejectWithValue(errorMessage)
      }

      return transformBootstrapData(data)
    } catch (err) {
      const errorMessage = err?.message || String(err)
      console.log('[Bootstrap] Fetch error:', errorMessage)
      return rejectWithValue(errorMessage)
    }
  }
)

const initialState = {
  loading: false,
  error: null,
  initialized: false
}

const bootstrapSlice = createSlice({
  name: 'bootstrap',
  initialState,
  reducers: {
    clearBootstrap: (state) => {
      state.loading = false
      state.error = null
      state.initialized = false
    },
    clearError: (state) => {
      state.error = null
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBootstrapData.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchBootstrapData.fulfilled, (state) => {
        state.loading = false
        state.initialized = true
      })
      .addCase(fetchBootstrapData.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
        state.initialized = false
      })
  }
})

export const { clearBootstrap, clearError } = bootstrapSlice.actions

// Selectors
export const selectBootstrapLoading = (state) => state.bootstrap.loading
export const selectBootstrapError = (state) => state.bootstrap.error
export const selectBootstrapInitialized = (state) => state.bootstrap.initialized

export default bootstrapSlice.reducer
